import { useLocation } from 'react-router';
import { NAFGTMEvent } from '@appTypes/GTM/NAFGTMEvent';
import { useGTMDataLayer } from '../../../hooks/GTM/useGTMDataLayer';
import { maskLicensePlateNumber } from '../../../hooks/VirtualPageViewTracking/maskLicensePlateNumber';
import { useMessageTrackingContext } from '../../../pages/MyMessages/contexts/MessageTrackingContext';

interface TrackProps {
  cta_position: string;
  text: string;
  url?: string;
  cta_hostname?: string;
  cta_url?: string;
  menuPage?: string;
  menuItemName?: string;
  menuItemUrl?: string;
  menuSectionName?: string;
}

export const useTrackCTA = () => {
  const location = useLocation();
  const dataLayer = useGTMDataLayer();
  const messageContext = useMessageTrackingContext();

  const trackCTA = ({
    cta_position,
    text,
    url,
    cta_hostname,
    menuPage,
    menuItemName,
    menuItemUrl,
    menuSectionName,
  }: TrackProps) => {
    const { href } = window.location;
    const currentUrlObject = new URL(href);

    // Mask license plate numbers in location pathname before sending to analytics.
    const maskedLocation = maskLicensePlateNumber(location.pathname);

    // Use message-specific event if within message context
    const eventPayload: Record<string, any> = {
      event: messageContext ? NAFGTMEvent.mittNafMessageCtaClick : 'cta_click',
      cta_text: text,
      cta_position,
      cta_location: maskedLocation,
      ...(menuPage ? { menuPage } : {}),
      ...(menuItemName ? { menuItemName } : {}),
      ...(menuItemUrl ? { menuItemUrl } : {}),
      ...(menuSectionName ? { menuSectionName } : {}),
      // Add message context - used for tracking CTA clicks within messages in Mitt NAF.
      ...(messageContext?.messageId ? { message_id: messageContext.messageId } : {}),
      ...(messageContext?.messageType ? { message_type: messageContext.messageType } : {}),
      ...(messageContext?.messageTitle ? { message_title: messageContext.messageTitle } : {}),
    };

    if (url) {
      let cta_url = url;
      if (url.startsWith('/')) {
        cta_url = `${window?.location?.origin}${url}`;
      }
      eventPayload.cta_url = maskLicensePlateNumber(cta_url);
    }

    if (cta_hostname) {
      eventPayload.cta_hostname = cta_hostname;
    } else if (url) {
      try {
        const urlObject = new URL(url.startsWith('/') ? `${window?.location?.origin}${url}` : url);
        eventPayload.cta_hostname = urlObject.hostname;
      } catch {
        eventPayload.cta_hostname = currentUrlObject.hostname;
      }
    } else {
      eventPayload.cta_hostname = currentUrlObject.hostname;
    }

    dataLayer?.push(eventPayload);
  };

  return trackCTA;
};
